import styled from '@emotion/styled';
import useWindowSize from '@hooks/useWindowSize';

import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import {
  Dialog,
  DialogHeader,
  DialogContent,
  DialogHeading
} from 'aquilla/core/molecules/Dialog';
import Cross from 'aquilla/icons/misc/Cross';
import throttle from 'lodash/throttle';
import { useEffect, useRef, useState } from 'react';
import AquillaDrawer from 'aquilla/core/organisms/Drawer';
import { getFirstLetterForIcon } from '@cont/SeeAll/utils';
import LeftChevron from 'aquilla/icons/actions/LeftChevron';
import { getIcon } from '@cont/NewPreSubscribed/utils';
import { pluralize } from '@utils/textHelper';
import RightChevron from 'aquilla/icons/actions/RightChevron';
import useCompeteInfoForAllMonths from '@cont/Compete/useCompeteInfoForAllMonths';
import {
  SYLLABUS_SECTIONS,
  SYLLABUS_SECTION_VIEWED
} from '@cont/Compete/constants';
import { getChapterCount, isFullSyllabus } from '@cont/Compete/utils';
import { noOp } from '@constants/empty';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useRouter } from 'next/router';
import Skeleton from 'aquilla/core/atoms/Skeleton';
import Month from './Month';

const StyledMonth = styled(Month)`
  width: 100%;
`;
const StyledDialog = styled(Dialog)`
  height: 528px;
`;

const StyAquillaDrawer = styled(AquillaDrawer)`
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-top: 0;
    & > .aquilla-drawer-header {
      padding: ${Spacings.SPACING_4B};
      width: 10px;
    }
    & > .aquilla-drawer-content {
      padding: 0;
    }
    max-height: 100vh;
  }
`;

const SubHeading = styled(Typography)`
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_2B};
  display: ${({ $hide }) => ($hide ? 'none' : '')};
  ${mediaQuery.uptoMobile} {
    margin-top: 0;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  & > div {
    margin-bottom: ${Spacings.SPACING_4B};
  }
  & > li {
    margin-bottom: ${Spacings.SPACING_2};
  }
`;

const StyledIconButton = styled.span`
  border: none;
  padding: 0;
  width: 20px;
  height: 20px;
  cursor: pointer;
  background-color: var(--color-white);
`;

const ListItem = styled.li`
  font-weight: ${Fonts.REGULAR};
  font-size: ${Fonts.P1};
  line-height: 20px;
  display: flex;
  align-items: center;
`;

const Disc = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  margin: 0 ${Spacings.SPACING_2B};
  background-color: var(--color-base-fill);
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderWrapper = styled.div`
  grid-column: span 24;
  display: flex;
  align-items: center;
  height: 56px;
  justify-content: left;
  width: 100%;
  position: sticky;
  z-index: 1;
  padding: ${Spacings.SPACING_4B};
  top: 0;
  background: var(--color-base-1);
  border-bottom: ${Spacings.SPACING_1} solid var(--color-divider);
`;

const Heading = styled(Typography)`
  margin-right: ${Spacings.SPACING_2B};
  margin-left: ${Spacings.SPACING_4B};
`;

const SubjectWrapper = styled.div`
  padding: ${Spacings.SPACING_4B};
  & > div {
    margin-bottom: ${Spacings.SPACING_4B};
  }
  & > li {
    margin-bottom: ${Spacings.SPACING_2};
  }
`;

const SubjectFlexRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
  height: fit-content;
  border: 1px solid var(--color-divider);
  border-radius: 8px;
  padding: ${Spacings.SPACING_4B};
  cursor: pointer;

  &:hover {
    background: var(--color-base-light-0);
  }
`;

const StyledP1 = styled(Typography)`
  font-weight: ${Fonts.SEMIBOLD};
`;

const ImageWrapper = styled.div`
  margin-right: ${Spacings.SPACING_4B};
  width: ${Spacings.SPACING_10B};
  height: ${Spacings.SPACING_10B};

  & > svg {
    width: ${Spacings.SPACING_10B};
    height: ${Spacings.SPACING_10B};
  }
`;

const Arrow = styled.div`
  display: flex;
  opacity: 0;
  transition: 0.3s ease;
  margin-right: -16px;
  ${SubjectFlexRow}:hover & {
    opacity: 1;
    transform: translateX(-16px);
  }
`;

const Flex = styled.div`
  display: flex;
`;

const StyledSkeleton = styled(Skeleton)`
  border-radius: 8px;
`;

const SyllabusModal = ({
  isSyllabusModalOpen,
  selectedItem,
  setSelectedItem,
  closeSyllabusModal,
  isSeeAllClicked
}) => {
  const [hideSubHeading, setHideSubHeading] = useState(false);
  const { syllabusForAllMonths, mutate, isValidating } =
    useCompeteInfoForAllMonths(isSeeAllClicked);
  const dialogRef = useRef(null);
  const dataFetchRef = useRef(null);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { isMobile } = useWindowSize();
  const [prevSelectedItem, setPrevSelectedItem] = useState({});
  const { goalUID } = useRouter();
  const goalSegmentData = useGoalEventProps({ goalUID });

  const hideSubHeadingOnScroll = () => {
    const contentElement = dialogRef.current;
    if (contentElement.scrollTop > 100) return setHideSubHeading(true);
    return setHideSubHeading(false);
  };

  const throttledScroll = throttle(hideSubHeadingOnScroll, 150);

  const renderTopics = (topics) => {
    return topics.map((topic, index) => {
      return (
        // eslint-disable-next-line react/no-array-index-key
        <ListItem key={index}>
          <Disc />
          {topic}
        </ListItem>
      );
    });
  };

  useEffect(() => {
    if (isLoggedIn && isSeeAllClicked && !dataFetchRef.current) {
      dataFetchRef.current = true;
      mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSeeAllClicked, isLoggedIn]);

  const handleSubjectClick = (subject) => {
    if (subject.totalChapters <= 0) {
      return noOp();
    }
    setPrevSelectedItem({ ...selectedItem });
    setSelectedItem({
      type: SYLLABUS_SECTIONS.SUBJECT,
      data: subject
    });
  };

  const renderSubjects = (subjects) => {
    return subjects?.map((subject) => {
      const { name, totalChapters, index, fullSyllabus } = subject;
      const letter = getFirstLetterForIcon(name);
      return (
        <SubjectFlexRow
          key={index}
          onClick={() => {
            handleSubjectClick(subject);
          }}
        >
          <Flex>
            <ImageWrapper>{getIcon(letter)}</ImageWrapper>

            <FlexColumn>
              <StyledP1 variant="p1">{name}</StyledP1>
              <Typography variant="p3" color="var(--color-text-secondary)">
                {fullSyllabus
                  ? 'Full syllabus'
                  : pluralize(totalChapters, 'chapter')}
              </Typography>
            </FlexColumn>
          </Flex>

          {totalChapters > 0 && (
            <Arrow>
              <RightChevron />
            </Arrow>
          )}
        </SubjectFlexRow>
      );
    });
  };

  const handleMonthClick = (syllabusData) => {
    setPrevSelectedItem({ ...selectedItem });
    setSelectedItem({
      type: SYLLABUS_SECTIONS.MONTH,
      data: syllabusData
    });
    segment.track(SYLLABUS_SECTION_VIEWED, { ...goalSegmentData });
  };

  const renderMonths = () => {
    const currentMonthIndex =
      syllabusForAllMonths?.findIndex((month) => month.soon) - 1;
    return syllabusForAllMonths?.map((syllabusData, index) => {
      const chapterCount = getChapterCount(syllabusData?.subjects);
      return (
        <StyledMonth
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          syllabus={syllabusData}
          handleMonthClick={() =>
            chapterCount > 0 ? handleMonthClick(syllabusData) : noOp
          }
          currentMonth={index === currentMonthIndex}
          pastMonth={index < currentMonthIndex}
        />
      );
    });
  };

  const renderHeading = () => {
    if (selectedItem?.type === SYLLABUS_SECTIONS.MONTH) {
      const {
        data: { month }
      } = selectedItem ?? {};

      return month;
    }

    if (selectedItem?.type === SYLLABUS_SECTIONS.SUBJECT) {
      const {
        data: { name: subjectName }
      } = selectedItem ?? {};

      return subjectName;
    }

    if (isSeeAllClicked) {
      return 'Syllabus';
    }
  };

  const renderSubheading = () => {
    if (selectedItem?.type === SYLLABUS_SECTIONS.MONTH) {
      const {
        data: { subjects }
      } = selectedItem ?? {};
      const fullSyllabus = isFullSyllabus(subjects);
      return fullSyllabus
        ? 'Full syllabus'
        : pluralize(getChapterCount(subjects), 'chapter') ?? 0;
    }

    if (selectedItem?.type === SYLLABUS_SECTIONS.SUBJECT) {
      const {
        data: { totalChapters, fullSyllabus }
      } = selectedItem ?? {};

      return fullSyllabus
        ? 'Full syllabus'
        : pluralize(totalChapters, 'chapter');
    }

    if (isSeeAllClicked) {
      return 'New chapters every month';
    }
  };

  const renderLoader = () => {
    return new Array(12).fill(0).map(() => {
      return (
        <>
          <StyledSkeleton width="100%" height="82px" variant="rect" />
        </>
      );
    });
  };

  const renderDialogContent = () => {
    if (selectedItem?.type === SYLLABUS_SECTIONS.MONTH) {
      const {
        data: { subjects }
      } = selectedItem ?? {};

      return renderSubjects(subjects);
    }
    if (selectedItem?.type === SYLLABUS_SECTIONS.SUBJECT) {
      const {
        data: { topics }
      } = selectedItem ?? {};
      return renderTopics(topics);
    }
    if (isSeeAllClicked && isValidating) {
      return renderLoader();
    }
    if (isSeeAllClicked) {
      return renderMonths();
    }
  };

  const renderIcon = () => {
    if (selectedItem?.type && selectedItem.level !== 1) {
      return (
        <StyledIconButton
          onClick={() => {
            setSelectedItem(prevSelectedItem ?? {});
            setPrevSelectedItem(null);
          }}
        >
          <LeftChevron height={20} width={20} />
        </StyledIconButton>
      );
    }
    return <Cross height={24} width={24} onClick={closeSyllabusModal} />;
  };

  if (isMobile) {
    return (
      <StyAquillaDrawer
        anchor="right"
        open={isSyllabusModalOpen}
        onClose={closeSyllabusModal}
        showCloseIcon={!isMobile}
      >
        <HeaderWrapper>
          <Cross onClick={closeSyllabusModal} height={24} width={24} />
          <Heading variant="h5">{renderHeading()}</Heading>
          <SubHeading variant="p2" $hide={hideSubHeading}>
            {renderSubheading()}
          </SubHeading>
        </HeaderWrapper>

        <SubjectWrapper>{renderDialogContent()}</SubjectWrapper>
      </StyAquillaDrawer>
    );
  }

  return (
    <>
      <StyledDialog
        size="small"
        open={isSyllabusModalOpen}
        close={closeSyllabusModal}
      >
        <DialogHeader>
          {renderIcon()}
          <DialogHeading>{renderHeading()}</DialogHeading>
          <SubHeading variant="p2" $hide={hideSubHeading}>
            {renderSubheading()}
          </SubHeading>
        </DialogHeader>
        <StyledDialogContent onScroll={throttledScroll} ref={dialogRef}>
          {renderDialogContent()}
        </StyledDialogContent>
      </StyledDialog>
    </>
  );
};

export default SyllabusModal;
